/**
 * Error logging
 *
 * Can be used to log errors to console or and eternal
 * error logging system, like Sentry for example.
 *
 */

import * as Sentry from '@sentry/browser';
import appSettings from '../config/settings';

const ingoreErrorsMap = {
  ['ResizeObserver loop limit exceeded']: true, // Some exotic browsers seems to emit these.
  ['Error reading']: true, // Ignore file reader errors (ImageFromFile)
  ['AxiosError: Network Error']: true,
};

const pickSelectedErrors = (ignored, entry) => {
  const [key, value] = entry;
  return value === true ? [...ignored, key] : ignored;
};

/**
 * Set up error handling. If a Sentry DSN is
 * provided a Sentry client will be installed.
 */
export const setup = () => {
  if (appSettings.sentryDsn) {
    const ignoreErrors = Object.entries(ingoreErrorsMap).reduce(pickSelectedErrors, []);

    // Configures the Sentry client. Adds a handler for
    // any uncaught exception.
    Sentry.init({
      dsn: appSettings.sentryDsn,
      environment: appSettings.env,
      ignoreErrors,
    });
  }
};

/**
 * Set user ID for the logger so that it
 * can be attached to Sentry issues.
 *
 * @param {String} userId ID of current user
 */
export const setUserId = userId => {
  Sentry.setUser({ id: userId });
};

/**
 * Clears the user ID.
 */

export const clearUserId = () => {
  Sentry.setUser(null);
};

/**
 * Add a breadcrumb to Sentry (no-op if Sentry disabled).
 *
 * @param {string} category
 * @param {string} message
 * @param {Object} [data]
 * @param {'fatal'|'error'|'warning'|'info'|'debug'} [level]
 */
export const breadcrumb = (category, message, data = {}, level = 'info') => {
  if (!appSettings.sentryDsn) {
    return;
  }
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level,
  });
};

const isDiagnosticNavLogEnabled = () =>
  process.env.NODE_ENV === 'development' ||
  process.env.REACT_APP_DIAGNOSTIC_NAV_LOGS === 'true';

/**
 * Path-only preview for diagnostics (no query, hash, or tokens).
 * @param {string} [pathOrUrl]
 * @returns {string}
 */
export const safePathPreviewForLog = pathOrUrl => {
  if (!pathOrUrl || typeof pathOrUrl !== 'string') {
    return '';
  }
  const trimmed = pathOrUrl.trim();
  if (!trimmed) {
    return '';
  }
  try {
    if (trimmed.startsWith('http')) {
      const u = new URL(trimmed);
      return u.pathname.slice(0, 120);
    }
    return trimmed.split('?')[0].split('#')[0].slice(0, 120);
  } catch (e) {
    return '';
  }
};

/**
 * Navigation / redirect decisions (landing marketing redirect, Auth0 return path, checkout confirm).
 * Console: development, or when REACT_APP_DIAGNOSTIC_NAV_LOGS=true (e.g. staging).
 * Sentry: breadcrumb when DSN is set (no secrets in data).
 *
 * @param {string} event
 * @param {Record<string, unknown>} [data]
 */
export const navigationDiagnostic = (event, data = {}) => {
  if (appSettings.sentryDsn) {
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: event,
      data,
      level: 'info',
    });
  }
  if (isDiagnosticNavLogEnabled() && typeof console !== 'undefined' && console.info) {
    console.info('[fyk-nav]', event, data);
  }
};

const printAPIErrorsAsConsoleTable = apiErrors => {
  if (apiErrors != null && apiErrors.length > 0 && typeof console.table === 'function') {
    console.table(apiErrors.map(err => ({ status: err.status, code: err.code, ...err.meta })));
  }
};

const responseAPIErrors = error => {
  return error && error.data && error.data.errors ? error.data.errors : [];
};

const responseApiErrorInfo = err =>
  responseAPIErrors(err).map(e => ({
    status: e.status,
    code: e.code,
    meta: e.meta,
  }));

/**
 * Logs an exception. If Sentry is configured
 * sends the error information there. Otherwise
 * prints the error to the console.
 *
 * @param {Error} e Error that occurred
 * @param {String} code Error code
 * @param {Object} data Additional data to be sent to Sentry
 */
export const error = (e, code, data) => {
  const apiErrors = responseApiErrorInfo(e);
  if (appSettings.sentryDsn) {
    const extra = { ...data, apiErrorData: apiErrors };

    Sentry.withScope(scope => {
      scope.setTag('code', code);
      if (extra.requestId) {
        scope.setTag('requestId', String(extra.requestId));
      }
      Object.keys(extra).forEach(key => {
        scope.setExtra(key, extra[key]);
      });
      Sentry.captureException(e);
    });

    printAPIErrorsAsConsoleTable(apiErrors);
  } else {
    console.error(e);
    console.error('Error code:', code, 'data:', data);
    printAPIErrorsAsConsoleTable(apiErrors);
  }
};

const setCause = (error, cause) => {
  const seenErrors = new WeakSet();

  const setCauseIfNoExistingCause = (error, cause) => {
    if (seenErrors.has(error)) {
      return;
    }
    if (error.cause) {
      seenErrors.add(error);
      return setCauseIfNoExistingCause(error.cause, cause);
    }
    error.cause = cause;
  };

  setCauseIfNoExistingCause(error, cause);
};

export const onRecoverableError = (err, componentStack) => {
  let data = {};

  if (componentStack) {
    // Generating this synthetic error allows monitoring services to apply sourcemaps
    // to unminify the stacktrace and make it readable.
    const errorBoundaryError = new Error(err.message);
    errorBoundaryError.name = `React ErrorBoundary ${errorBoundaryError.name}`;
    errorBoundaryError.stack = componentStack;

    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause
    setCause(err, errorBoundaryError);

    data.componentStack = componentStack;
    // In development, log the full component stack to console so hydration (#418) can be traced.
    if (process.env.NODE_ENV === 'development' && typeof console !== 'undefined') {
      console.warn(
        '[React hydration] Recoverable error (often #418 = server/client DOM mismatch). Component stack:\n',
        componentStack
      );
    }
  }

  // Replace with your error monitoring service.
  error(err, 'recoverable-error', data);
};
